
import { motion } from 'framer-motion';

export default function TestimonialSection() {
  return (
    <section className="relative py-32 overflow-hidden bg-dark-deep">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neon-purple/5 to-transparent"></div>
      
      <div className="container mx-auto px-10 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="inline-block text-xs font-bold text-neon-green border border-neon-green/30 rounded-full px-4 py-1.5 mb-5">
            실제 이용자 후기
          </span>
          <h2 className="text-5xl font-display font-black text-white mb-5">
            온니잡으로 <span className="text-transparent bg-clip-text bg-gradient-neon">새로운 기회</span>를 찾았어요
          </h2>
          <p className="text-base text-gray-400 max-w-2xl mx-auto">
            나이트라이프 업계 종사자들의 생생한 경험담을 들어보세요
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative max-w-6xl mx-auto"
        >
          {/* Decorative Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="testimonial-pattern" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
                  <circle cx="100" cy="100" r="2" fill="#a855f7" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#testimonial-pattern)" />
            </svg>
          </div>

          {/* Decorative Lines */}
          <div className="absolute -left-20 top-1/2 -translate-y-1/2 opacity-20">
            <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 20 Q 60 0, 100 20 T 180 20" stroke="#2d1b4e" strokeWidth="2" />
              <path d="M20 50 Q 60 20, 100 50 T 180 50" stroke="#2d1b4e" strokeWidth="2" />
              <path d="M20 80 Q 60 50, 100 80 T 180 80" stroke="#2d1b4e" strokeWidth="2" />
              <path d="M20 110 Q 60 80, 100 110 T 180 110" stroke="#2d1b4e" strokeWidth="2" />
            </svg>
          </div>

          <div className="grid grid-cols-5 gap-12 items-center relative z-10">
            {/* Left Profile Image */}
            <div className="col-span-2">
              <div className="w-full h-[480px] rounded-[32px] overflow-hidden shadow-[0_16px_48px_rgba(0,0,0,0.6)] -ml-32">
                {/* Image removed */}
              </div>
            </div>

            {/* Right Content */}
            <div className="col-span-3">
              {/* Quote Icon */}
              <div className="mb-8">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-neon/10 border border-neon-purple/20">
                  <i className="ri-double-quotes-l text-3xl text-neon-purple w-8 h-8 flex items-center justify-center"></i>
                </div>
              </div>

              {/* Testimonial Text */}
              <blockquote className="mb-10">
                <p className="text-2xl font-medium text-white leading-relaxed mb-6">
                  온니잡 덕분에 제가 원하던 강남의 모던바에 취업할 수 있었어요. 
                  <span className="text-neon-green"> 상세한 채용 정보</span>와 
                  <span className="text-neon-purple"> 실제 근무 환경</span>을 미리 확인할 수 있어서 
                  정말 만족스러운 선택을 했습니다.
                </p>
                <p className="text-base text-gray-400 leading-relaxed">
                  이전에는 구인 정보를 찾기가 너무 어려웠는데, 나이트잡은 업계 전문 플랫폼이라 
                  신뢰할 수 있는 정보만 모여있어요. 커뮤니티에서 선배들의 조언도 들을 수 있고, 
                  면접 준비도 훨씬 수월했습니다.
                </p>
              </blockquote>

              {/* Author Info */}
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-neon-purple/30 shadow-lg">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/994698ca99ffa3964ce3d38761334638.jpg" 
                    alt="김민지" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white mb-1">김민지</h4>
                  <p className="text-sm text-gray-400">바텐더 · 강남 모던바 라운지</p>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-8 mt-10 pt-10 border-t border-white/10">
                <div>
                  <div className="text-3xl font-black text-neon-green mb-1">2주</div>
                  <div className="text-xs text-gray-500">취업 소요 기간</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-neon-purple mb-1">5개</div>
                  <div className="text-xs text-gray-500">지원한 공고 수</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-white mb-1">100%</div>
                  <div className="text-xs text-gray-500">만족도</div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Dots */}
          <div className="flex items-center justify-center gap-2 mt-16">
            <button className="w-3 h-3 rounded-full bg-neon-purple cursor-pointer"></button>
            <button className="w-3 h-3 rounded-full bg-white/20 hover:bg-white/40 transition-colors cursor-pointer"></button>
            <button className="w-3 h-3 rounded-full bg-white/20 hover:bg-white/40 transition-colors cursor-pointer"></button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
